import { z } from 'zod';
import { adminApiFetch } from './apiService';
import { apiSuccess, apiList, parseAdminApiResponse } from '@services/apiSchemas';

// Schema mirrors Api::Admin::V1::InventoryTypeReclassifyRulesController#serialize
// (app/controllers/api/admin/v1/inventory_type_reclassify_rules_controller.rb).
// All object schemas use .passthrough() so backend additions don't break parsing —
// drift detection comes from missing/typed fields, not extras.

// Tier 採下限語意（與 sales_channel_rule.original_expiration_date_threshold 對齊）：
// `original_expiration_date_threshold` 代表此 tier 適用 product.validity_days 的下限（含）。
// 同一 rule 內以 threshold 升冪排序後，每階覆蓋 [threshold, next.threshold) 範圍，
// 最高 threshold 那階開上無界。最低 threshold（通常 0）即為 small-end catch-all。
// expiration_ratio 採 0~100 百分比（與 sales_channel_rule 一致），非 0~1 小數。
const TierSchema = z.object({
  id: z.string().optional(),
  original_expiration_date_threshold: z.number().int().nonnegative(),
  trigger_mechanism: z.enum([ 'by_days', 'by_ratio' ]),
  days_before_expiration: z.number().int().positive().nullable(),
  expiration_ratio: z.number().positive().max(100).nullable(),
}).passthrough().refine(
  (t) => {
    if (t.trigger_mechanism === 'by_days') {
      return t.days_before_expiration !== null && t.expiration_ratio === null;
    }
    return t.expiration_ratio !== null && t.days_before_expiration === null;
  },
  { message: 'tier mechanism / threshold mismatch' },
);

const InventoryTypeMiniSchema = z.object({
  id: z.string(),
  name: z.string(),
}).passthrough();

const OwnerSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
}).passthrough();

export const RuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  priority: z.number().int(),
  enabled: z.boolean(),
  source_inventory_type_id: z.string(),
  target_inventory_type_id: z.string(),
  source_inventory_type: InventoryTypeMiniSchema.optional().nullable(),
  target_inventory_type: InventoryTypeMiniSchema.optional().nullable(),
  owner_merchant_user: OwnerSchema.optional().nullable(),
  tiers: z.array(TierSchema),
}).passthrough();

export type Tier = z.infer<typeof TierSchema>;
export type Rule = z.infer<typeof RuleSchema>;

export interface RuleInput {
  name: string;
  priority?: number;
  enabled?: boolean;
  source_inventory_type_id: string;
  target_inventory_type_id: string;
  tiers_attributes: Array<{
    id?: string;
    original_expiration_date_threshold: number;
    trigger_mechanism: 'by_days' | 'by_ratio';
    days_before_expiration: number | null;
    expiration_ratio: number | null;
    _destroy?: boolean;
  }>;
}

// Controller#index uses render_array_list — the envelope is the standard list
// shape `{ success, message, data: [...], has_more: false }`, so callers unwrap
// via `result.data` (not `result.data.data`). get_records uses render_data_list
// and behaves like a normal paginated list response.
export const inventoryTypeReclassifyRuleService = {
  index: async () => {
    const res = await adminApiFetch('inventory_type_reclassify_rules');
    return parseAdminApiResponse(res, apiList(RuleSchema));
  },

  getRecords: async (params: Record<string, unknown>) => {
    const res = await adminApiFetch('inventory_type_reclassify_rules/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return parseAdminApiResponse(res, apiList(RuleSchema));
  },

  show: async (id: string) => {
    const res = await adminApiFetch(`inventory_type_reclassify_rules/${id}`);
    return parseAdminApiResponse(res, apiSuccess(RuleSchema));
  },

  create: async (input: RuleInput) => {
    const res = await adminApiFetch('inventory_type_reclassify_rules', {
      method: 'POST',
      body: JSON.stringify({ inventory_type_reclassify_rule: input }),
    });
    return parseAdminApiResponse(res, apiSuccess(RuleSchema));
  },

  update: async (id: string, input: Partial<RuleInput>) => {
    const res = await adminApiFetch(`inventory_type_reclassify_rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ inventory_type_reclassify_rule: input }),
    });
    return parseAdminApiResponse(res, apiSuccess(RuleSchema));
  },

  destroy: async (id: string) => {
    const res = await adminApiFetch(`inventory_type_reclassify_rules/${id}`, {
      method: 'DELETE',
    });
    // Controller responds 204 No Content. parseAdminApiResponse would choke on
    // an empty body, so short-circuit here with a synthetic success envelope.
    if (res.status === 204) return { success: true as const, data: null };
    return parseAdminApiResponse(res, apiSuccess(z.null().or(z.object({}).passthrough())));
  },
};

export default inventoryTypeReclassifyRuleService;
